import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select } from "@/components/ui/form";
import { Button, buttonVariants } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { cn } from "@/lib/utils";
import { Sheet, ConfirmDialog, FieldError, FieldRow, Switch, SectionHeading, Muted } from "@/components/ui/overlays";
import { useAuth } from "@/app/AuthContext";
import { useToast } from "@/app/Toast";
import { Plus, Pencil, Trash2, Archive, RefreshCw, CalendarDays } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { DateTime } from "luxon";
import { CustomerValuePage } from "./CustomerValue";

interface LocationRow {
  id: number; name: string; slug: string; timezone: string; currency: string;
  addressLine1: string | null; addressLine2: string | null; city: string | null;
  region: string | null; postalCode: string | null; countryCode: string | null;
  contactPhone: string | null; contactEmail: string | null; bookingHorizonDays: number; active: boolean;
}
interface ServiceRow {
  id: number; locationId: number; code: string; name: string; description: string | null;
  durationMinutes: number; priceCents: number; active: boolean;
}
interface ResourceRow { id: number; locationId: number; name: string; type: string; staffUserId: number | null; homeLocationId: number | null; active: boolean; defaultStaffUserIds: number[]; }
interface HoursRow { id: number; dayOfWeek: number; sequence: number; openTime: string; closeTime: string; isClosed: boolean; }
interface BlackoutRow { id: number; locationId: number; resourceId: number | null; resourceName: string | null; blackoutSeriesId: number | null; occurrenceLocalDate: string | null; source: "SINGLE" | "SERIES"; startsAtUtc: string; endsAtUtc: string; reason: string | null; }
interface BlackoutSeriesRow { id: number; locationId: number; resourceId: number | null; resourceName: string | null; timezone: string; startLocalDate: string; startLocalTime: string; endLocalTime: string; rrule: string; reason: string | null; status: "ACTIVE" | "CANCELLED"; materializedThroughLocalDate: string | null; }
interface UserRow { id: number; email: string; firstName: string; lastName: string; role: "ADMIN" | "MANAGER" | "STAFF"; active: boolean; lastLoginAt: string | null; createdAt: string; memberships: Array<{ locationId: number; scope: "READ" | "MANAGE" }>; }

type Tab = "general" | "services" | "addons" | "resources" | "hours" | "blackouts" | "cancellation" | "recurring" | "customer-value" | "users";

const TABS: Array<{ id: Tab; label: string; adminOnly?: boolean }> = [
  { id: "general", label: "General" },
  { id: "services", label: "Services" },
  { id: "addons", label: "Add-ons" },
  { id: "resources", label: "Bays" },
  { id: "hours", label: "Hours" },
  { id: "blackouts", label: "Blackouts" },
  { id: "cancellation", label: "Cancellation", adminOnly: true },
  { id: "recurring", label: "Recurring" },
  { id: "customer-value", label: "Customer value" },
  { id: "users", label: "Users", adminOnly: true },
];

const DOWS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function SettingsPage() {
  const { hasRole } = useAuth();
  const { tab } = useParams();
  const nav = useNavigate();
  const currentTab = (TABS.find((t) => t.id === tab)?.id ?? "general") as Tab;

  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<LocationRow[]>("/api/v1/locations").then((l) => {
      setLocations(l);
      if (l[0]) setLocationId(l[0].id);
    }).catch((e) => setError(e.message));
  }, []);

  const visibleTabs = TABS.filter((t) => !t.adminOnly || hasRole("ADMIN"));

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {locations.length > 0 && currentTab !== "users" && (
        <Card>
          <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold">Editing location</p>
              <p className="text-xs text-muted-foreground">Choose a location before changing its settings.</p>
            </div>
            <Select className="sm:w-80" value={locationId?.toString() ?? ""} onChange={(e) => setLocationId(Number(e.target.value))}>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.active ? "" : " · archived"}</option>)}
            </Select>
          </CardContent>
        </Card>
      )}
      <div className="flex flex-wrap gap-1 border-b">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => nav(`/settings/${t.id}`)}
            className={cn(
              "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
              currentTab === t.id
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {!locationId && currentTab !== "users" && currentTab !== "general" && (
        <Card><CardContent className="p-6 text-sm text-muted-foreground">No locations configured.</CardContent></Card>
      )}
      {currentTab === "general" && (
        <GeneralTab
          locationId={locationId}
          locations={locations}
          setLocations={setLocations}
          setLocationId={setLocationId}
          isAdmin={hasRole("ADMIN")}
        />
      )}
      {currentTab === "services" && locationId && <ServicesTab locationId={locationId} />}
      {currentTab === "addons" && locationId && <AddonsTab locationId={locationId} />}
      {currentTab === "resources" && locationId && <ResourcesTab locationId={locationId} />}
      {currentTab === "hours" && locationId && <HoursTab locationId={locationId} />}
      {currentTab === "blackouts" && locationId && <BlackoutsTab locationId={locationId} timezone={locations.find((location) => location.id === locationId)?.timezone ?? "UTC"} />}
      {currentTab === "cancellation" && locationId && <CancellationTab locationId={locationId} />}
      {currentTab === "recurring" && <RecurringTab />}
      {currentTab === "customer-value" && locationId && <CustomerValuePage locationId={locationId} />}
      {currentTab === "users" && <UsersTab locations={locations} />}
    </div>
  );
}

/* ---------- General ---------- */

function GeneralTab({
  locationId,
  locations,
  setLocations,
  setLocationId,
  isAdmin,
}: {
  locationId: number | null;
  locations: LocationRow[];
  setLocations: (locations: LocationRow[]) => void;
  setLocationId: (id: number | null) => void;
  isAdmin: boolean;
}) {
  const { toast } = useToast();
  const loc = locations.find((l) => l.id === locationId);
  const [form, setForm] = useState(() => locationToForm(loc));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    if (loc) {
      setForm(locationToForm(loc));
      setEditing(false);
    }
  }, [loc?.id]);

  async function refresh(selectedId?: number) {
    const refreshed = await api.get<LocationRow[]>("/api/v1/locations");
    setLocations(refreshed);
    if (selectedId !== undefined) setLocationId(selectedId);
    else if (!refreshed.some((location) => location.id === locationId)) setLocationId(refreshed[0]?.id ?? null);
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!loc) return;
    setSaving(true);
    try {
      await api.patch(`/api/v1/locations/${loc.id}`, form);
      await refresh(loc.id);
      toast("success", "Location saved");
      setEditing(false);
    } catch (err) {
      toast("error", apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  async function restore() {
    if (!loc) return;
    try {
      await api.patch(`/api/v1/locations/${loc.id}`, { active: true });
      await refresh(loc.id);
      toast("success", "Location restored");
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Restore failed");
    }
  }

  async function archive() {
    if (!loc) return;
    try {
      await api.delete(`/api/v1/locations/${loc.id}`);
      await refresh(loc.id);
      toast("success", "Location archived");
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Archive failed");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <SectionHeading>Locations</SectionHeading>
          <Muted>Manage business identity, regional settings, and booking availability.</Muted>
        </div>
        {isAdmin && <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New location</Button>}
      </div>
      {!loc ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No locations configured. Create one to begin scheduling services.</CardContent></Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">{loc.name}</CardTitle>
                {!loc.active && <Badge>ARCHIVED</Badge>}
              </div>
              {isAdmin && !editing && (
                <div className="flex gap-2">
                  {loc.active ? (
                    <Button variant="outline" size="sm" onClick={() => setArchiving(true)}><Archive className="h-3.5 w-3.5" /> Archive</Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => void restore()}><RefreshCw className="h-3.5 w-3.5" /> Restore</Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {editing ? <form onSubmit={save} className="grid gap-4 md:grid-cols-2">
          <FieldRow>
            <Label>Name</Label>
            <Input required value={form.name} disabled={!editing} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Slug</Label>
            <Input required value={form.slug} disabled={!editing} pattern="[a-z0-9-]+" onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
            <Muted>Unique URL-safe identifier.</Muted>
          </FieldRow>
          <FieldRow>
             <Label>Currency (ISO 4217)</Label>
             <Input required pattern="[A-Z]{3}" value={form.currency} disabled={!editing} maxLength={3} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          </FieldRow>
          <FieldRow>
            <Label>Standard booking horizon (days)</Label>
            <Input type="number" min="1" max="365" value={form.bookingHorizonDays} disabled={!editing} onChange={(e) => setForm({ ...form, bookingHorizonDays: Number(e.target.value) })} />
            <Muted>Priority memberships can extend this window.</Muted>
          </FieldRow>
          <FieldRow className="md:col-span-2">
             <Label>IANA timezone</Label>
            <Input value={form.timezone} disabled={!editing} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            <FieldError message={form.timezone && !isIana(form.timezone) ? "Invalid IANA name (e.g. Asia/Colombo)" : undefined} />
          </FieldRow>
          <FieldRow className="md:col-span-2">
            <Label>Address line 1</Label>
            <Input value={form.addressLine1} disabled={!editing} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>City</Label>
            <Input value={form.city} disabled={!editing} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Region / State</Label>
            <Input value={form.region} disabled={!editing} onChange={(e) => setForm({ ...form, region: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Postal code</Label>
            <Input value={form.postalCode} disabled={!editing} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Country code (ISO 3166-1 alpha-2)</Label>
            <Input maxLength={2} value={form.countryCode} disabled={!editing} onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })} />
          </FieldRow>
          <FieldRow>
            <Label>Contact phone</Label>
            <Input value={form.contactPhone} disabled={!editing} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Contact email</Label>
            <Input type="email" value={form.contactEmail} disabled={!editing} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
          </FieldRow>
          <FieldRow className="flex-row flex items-center gap-3 md:col-span-2">
            <Switch id="active" checked={form.active} disabled={!editing} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            <Label htmlFor="active">Accepting bookings at this location</Label>
          </FieldRow>
          {editing && (
            <div className="flex justify-end gap-2 md:col-span-2">
              <Button type="button" variant="outline" onClick={() => { setForm(locationToForm(loc)); setEditing(false); }}>Cancel</Button>
               <Button type="submit" disabled={saving || !isIana(form.timezone) || !isCurrencyCode(form.currency)}>{saving ? "Saving…" : "Save"}</Button>
            </div>
          )}
            </form> : <LocationSummary location={loc} />}
          </CardContent>
        </Card>
      )}
      <LocationCreateSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={async (id) => { setCreating(false); await refresh(id); }}
      />
      <ConfirmDialog
        open={archiving}
        onClose={() => setArchiving(false)}
        onConfirm={archive}
        title="Archive location?"
        description="The location will stop accepting bookings but its history remains available. Future bookings and waiting customers must be resolved first."
        confirmLabel="Archive location"
        destructive
      />
    </div>
  );
}

type LocationFormState = ReturnType<typeof locationToForm>;

function locationToForm(location?: LocationRow) {
  return {
    name: location?.name ?? "",
    slug: location?.slug ?? "",
    timezone: location?.timezone ?? "UTC",
    currency: location?.currency ?? "USD",
    addressLine1: location?.addressLine1 ?? "",
    addressLine2: location?.addressLine2 ?? "",
    city: location?.city ?? "",
    region: location?.region ?? "",
    postalCode: location?.postalCode ?? "",
    countryCode: location?.countryCode ?? "",
    contactPhone: location?.contactPhone ?? "",
    contactEmail: location?.contactEmail ?? "",
    bookingHorizonDays: location?.bookingHorizonDays ?? 30,
    active: location?.active ?? true,
  };
}

function LocationSummary({ location }: { location: LocationRow }) {
  const address = [location.addressLine1, location.city, location.region, location.postalCode].filter(Boolean).join(", ");
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Location name</p>
        <p className="mt-1 font-medium">{location.name}</p>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</p>
        <p className="mt-1 font-medium">{location.active ? "Accepting bookings" : "Archived"}</p>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Timezone</p>
        <p className="mt-1 font-medium">{location.timezone}</p>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Currency</p>
        <p className="mt-1 font-medium">{location.currency}</p>
      </div>
      <div className="sm:col-span-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Address</p>
        <p className="mt-1 font-medium">{address || "Not added"}</p>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contact phone</p>
        <p className="mt-1 font-medium">{location.contactPhone || "Not added"}</p>
      </div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contact email</p>
        <p className="mt-1 font-medium">{location.contactEmail || "Not added"}</p>
      </div>
      <div className="border-t pt-4 sm:col-span-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Booking horizon</p>
        <p className="mt-1 font-medium">Bookings can be made up to {location.bookingHorizonDays} days ahead.</p>
      </div>
    </div>
  );
}

function LocationCreateSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: number) => void | Promise<void> }) {
  const { toast } = useToast();
  const [form, setForm] = useState<LocationFormState>(() => locationToForm());
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(locationToForm());
    setSlugTouched(false);
  }, [open]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const created = await api.post<{ id: number }>("/api/v1/locations", form);
      toast("success", "Location created");
      await onCreated(created.id);
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="New location" description="Create a bookable business location.">
      <form onSubmit={save} className="space-y-4">
        <FieldRow>
          <Label>Name</Label>
          <Input required value={form.name} onChange={(e) => {
            const name = e.target.value;
            setForm({ ...form, name, slug: slugTouched ? form.slug : slugify(name) });
          }} />
        </FieldRow>
        <FieldRow>
          <Label>Slug</Label>
          <Input required pattern="[a-z0-9-]+" value={form.slug} onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: slugify(e.target.value) }); }} />
          <Muted>Unique URL-safe identifier.</Muted>
        </FieldRow>
        <div className="grid gap-3 sm:grid-cols-2">
          <FieldRow>
            <Label>IANA timezone</Label>
            <Input required value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
            <FieldError message={form.timezone && !isIana(form.timezone) ? "Invalid IANA name" : undefined} />
          </FieldRow>
          <FieldRow>
            <Label>Currency</Label>
            <Input required minLength={3} maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          </FieldRow>
        </div>
        <FieldRow>
          <Label>Standard booking horizon (days)</Label>
          <Input required type="number" min="1" max="365" value={form.bookingHorizonDays} onChange={(e) => setForm({ ...form, bookingHorizonDays: Number(e.target.value) })} />
        </FieldRow>
        <FieldRow>
          <Label>Address line 1</Label>
          <Input value={form.addressLine1} onChange={(e) => setForm({ ...form, addressLine1: e.target.value })} />
        </FieldRow>
        <div className="grid gap-3 sm:grid-cols-2">
          <FieldRow>
            <Label>City</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Country code</Label>
            <Input maxLength={2} value={form.countryCode} onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })} />
          </FieldRow>
        </div>
        <FieldRow>
          <Label>Contact phone</Label>
          <Input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
        </FieldRow>
        <FieldRow>
          <Label>Contact email</Label>
          <Input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
        </FieldRow>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving || !isIana(form.timezone)}>{saving ? "Creating…" : "Create location"}</Button>
        </div>
      </form>
    </Sheet>
  );
}

function slugify(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function isIana(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function isCurrencyCode(value: string): boolean {
  return /^[A-Z]{3}$/.test(value);
}

/* ---------- Services ---------- */

function ServicesTab({ locationId }: { locationId: number }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get<ServiceRow[]>(`/api/v1/ops/services?locationId=${locationId}`).then(setRows).catch(() => setRows([]));
  }, [locationId, refreshKey]);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">Services</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New service</Button>
        </div>
        <Muted>Service prices and durations are snapshotted onto each booking at creation time.</Muted>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-mono">{s.code}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">{s.description}</TableCell>
                <TableCell>{s.durationMinutes}m</TableCell>
                <TableCell className="text-right tabular-nums">{(s.priceCents / 100).toFixed(2)}</TableCell>
                <TableCell>{s.active ? "✓" : <Badge>archived</Badge>}</TableCell>
                <TableCell className="text-right">
                  <button type="button" className="rounded p-1 hover:bg-accent" onClick={() => setEditing(s)} aria-label="Edit">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  {s.active && (
                    <button
                      type="button"
                      className="rounded p-1 hover:bg-accent"
                      onClick={async () => {
                        if (!confirm("Archive this service? It will no longer appear for new bookings.")) return;
                        try {
                          await api.patch(`/api/v1/ops/services/${s.id}`, { active: false });
                          toast("success", `${s.name} archived`);
                          setRefreshKey((k) => k + 1);
                        } catch (err) {
                          toast("error", err instanceof Error ? err.message : "Archive failed");
                        }
                      }}
                      aria-label="Archive"
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No services yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      <ServiceSheet
        open={creating || editing != null}
        initial={editing}
        locationId={locationId}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setRefreshKey((k) => k + 1); setEditing(null); setCreating(false); }}
      />
    </Card>
  );
}

function ServiceSheet({
  open, initial, locationId, onClose, onSaved,
}: {
  open: boolean;
  initial: ServiceRow | null;
  locationId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isEdit = initial != null;
  const [form, setForm] = useState({
    code: initial?.code ?? "",
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    durationMinutes: initial?.durationMinutes ?? 30,
    priceCents: initial?.priceCents ?? 1000,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setForm({
        code: initial.code,
        name: initial.name,
        description: initial.description ?? "",
        durationMinutes: initial.durationMinutes,
        priceCents: initial.priceCents,
        active: initial.active,
      });
    } else {
      setForm({ code: "", name: "", description: "", durationMinutes: 30, priceCents: 1000, active: true });
    }
    setErr(null);
  }, [initial, open]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const body = { locationId, ...form };
      if (isEdit) await api.patch(`/api/v1/ops/services/${initial!.id}`, form);
      else await api.post("/api/v1/ops/services", body);
      toast("success", isEdit ? "Service updated" : "Service created");
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title={isEdit ? "Edit service" : "New service"} description="Prices and durations are snapshotted onto each booking at creation time.">
      <form onSubmit={save} className="space-y-3">
        {err && <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</p>}
        <FieldRow>
          <Label>Code</Label>
          <Input required maxLength={40} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          <Muted>Short identifier (e.g. EXPRESS, PREMIUM). Unique per location.</Muted>
        </FieldRow>
        <FieldRow>
          <Label>Display name</Label>
          <Input required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FieldRow>
        <FieldRow>
          <Label>Description</Label>
          <Input value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </FieldRow>
        <div className="grid gap-3 sm:grid-cols-2">
          <FieldRow>
            <Label>Duration (minutes)</Label>
            <Input type="number" min={1} max={1440} value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
          </FieldRow>
          <FieldRow>
            <Label>Price (minor units)</Label>
            <Input type="number" min={0} value={form.priceCents} onChange={(e) => setForm({ ...form, priceCents: Number(e.target.value) })} />
            <Muted>{(form.priceCents / 100).toFixed(2)} in major units</Muted>
          </FieldRow>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="svc-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          <Label htmlFor="svc-active">Active (bookable)</Label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Resources (bays) ---------- */

function ResourcesTab({ locationId }: { locationId: number }) {
  const [rows, setRows] = useState<ResourceRow[]>([]);
  const [editing, setEditing] = useState<ResourceRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [users, setUsers] = useState<UserRow[]>([]);

  useEffect(() => {
    api.get<ResourceRow[]>(`/api/v1/ops/resources?locationId=${locationId}`).then(setRows).catch(() => setRows([]));
  }, [locationId, refreshKey]);
  useEffect(() => { api.get<UserRow[]>("/api/v1/users").then(setUsers).catch(() => setUsers([])); }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">Bays / Resources</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New resource</Button>
        </div>
        <Muted>Bookings can lock a primary bay or facility plus multiple STAFF_MEMBER resources. Deactivated resources keep their history.</Muted>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Staff account</TableHead>
              <TableHead>Default staff</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.name}</TableCell>
                <TableCell className="font-mono text-xs">{r.type}</TableCell>
                <TableCell>{users.find((user) => user.id === r.staffUserId)?.email ?? "—"}</TableCell>
                <TableCell>{r.defaultStaffUserIds.length ? `${r.defaultStaffUserIds.length} assigned` : "—"}</TableCell>
                <TableCell>{r.active ? "✓" : <Badge>deactivated</Badge>}</TableCell>
                <TableCell className="text-right">
                  <button className="rounded p-1 hover:bg-accent" onClick={() => setEditing(r)} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                </TableCell>
              </TableRow>
            ))}
             {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No bays yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <ResourceSheet
        open={creating || editing != null}
        initial={editing}
        locationId={locationId}
        users={users}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setRefreshKey((k) => k + 1); setEditing(null); setCreating(false); }}
      />
    </Card>
  );
}

function ResourceSheet({ open, initial, locationId, users, onClose, onSaved }: {
  open: boolean; initial: ResourceRow | null; locationId: number;
  users: UserRow[];
  onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const isEdit = initial != null;
  const [form, setForm] = useState({ name: initial?.name ?? "", type: initial?.type ?? "BAY", staffUserId: initial?.staffUserId ?? null as number | null, defaultStaffUserIds: initial?.defaultStaffUserIds ?? [] as number[], homeLocationId: initial?.homeLocationId ?? locationId, active: initial?.active ?? true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
     if (initial) setForm({ name: initial.name, type: initial.type, staffUserId: initial.staffUserId, defaultStaffUserIds: initial.defaultStaffUserIds, homeLocationId: initial.homeLocationId ?? locationId, active: initial.active });
     else setForm({ name: "", type: "BAY", staffUserId: null, defaultStaffUserIds: [], homeLocationId: locationId, active: true });
  }, [initial, open]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/api/v1/ops/resources/${initial!.id}`, form);
      else await api.post(`/api/v1/ops/resources`, { locationId, ...form });
      toast("success", isEdit ? "Bay updated" : "Bay created");
      onSaved();
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title={isEdit ? "Edit bay" : "New bay"}>
      <form onSubmit={save} className="space-y-3">
        <FieldRow>
          <Label>Name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FieldRow>
        {form.type !== "STAFF_MEMBER" && <FieldRow>
          <Label>Default staff for this bay</Label>
          <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-2">
            {users.filter((user) => user.active && user.role !== "ADMIN").map((user) => <label key={user.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.defaultStaffUserIds.includes(user.id)} onChange={(event) => setForm({ ...form, defaultStaffUserIds: event.currentTarget.checked ? [...form.defaultStaffUserIds, user.id] : form.defaultStaffUserIds.filter((id) => id !== user.id) })} />
              {user.firstName} {user.lastName}
            </label>)}
          </div>
          <Muted>These staff members are preselected for new bookings using this bay. Staff can change them per booking.</Muted>
        </FieldRow>}
        <FieldRow>
          <Label>Type</Label>
          <Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
          <Muted>Use STAFF_MEMBER for assignable people; BAY, DETAIL_BAY, or MOTORBIKE_BAY for facilities.</Muted>
        </FieldRow>
        <FieldRow>
          <Label>Staff account</Label>
          <Select value={form.staffUserId ?? ""} onChange={(e) => setForm({ ...form, staffUserId: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Not linked</option>
            {users.filter((user) => user.active).map((user) => <option key={user.id} value={user.id}>{user.firstName} {user.lastName} · {user.email}</option>)}
          </Select>
          <Muted>Link STAFF_MEMBER resources to the account used for calendar feeds.</Muted>
        </FieldRow>
        <div className="flex items-center gap-3">
          <Switch id="res-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          <Label htmlFor="res-active">Active (bookable)</Label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Hours ---------- */

interface DailyHours { dayOfWeek: number; isClosed: boolean; shifts: Array<{ openTime: string; closeTime: string }>; }

function HoursTab({ locationId }: { locationId: number }) {
  const { toast } = useToast();
  const [hours, setHours] = useState<DailyHours[]>(defaultHours());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    api.get<HoursRow[]>(`/api/v1/ops/business-hours?locationId=${locationId}`)
      .then((rows) => {
        const grouped = new Map<number, DailyHours>();
        for (const r of rows) {
          const day = grouped.get(r.dayOfWeek) ?? { dayOfWeek: r.dayOfWeek, isClosed: r.isClosed, shifts: [] };
          day.isClosed = day.isClosed && r.isClosed;
          if (!r.isClosed) day.shifts.push({ openTime: r.openTime?.slice(0, 5) ?? "09:00", closeTime: r.closeTime?.slice(0, 5) ?? "18:00" });
          grouped.set(r.dayOfWeek, day);
        }
        for (let d = 0; d < 7; d++) {
          if (!grouped.has(d)) {
            grouped.set(d, { dayOfWeek: d, isClosed: d === 0, shifts: [{ openTime: "09:00", closeTime: "18:00" }] });
          }
        }
        const out = Array.from(grouped.values()).sort((a, b) => a.dayOfWeek - b.dayOfWeek);
        setHours(out);
      })
      .catch(() => setHours(defaultHours()))
      .finally(() => setLoading(false));
  }, [locationId, refreshKey]);

  function update(d: number, patch: Partial<DailyHours>) {
    setHours((curr) => curr.map((h) => h.dayOfWeek === d ? { ...h, ...patch } : h));
  }

  function updateShift(dayOfWeek: number, index: number, patch: Partial<DailyHours["shifts"][number]>) {
    setHours((current) => current.map((day) => day.dayOfWeek !== dayOfWeek ? day : {
      ...day,
      shifts: day.shifts.map((shift, shiftIndex) => shiftIndex === index ? { ...shift, ...patch } : shift),
    }));
  }

  function addShift(dayOfWeek: number) {
    const day = hours.find((item) => item.dayOfWeek === dayOfWeek);
    const last = day?.shifts.at(-1);
    const openTime = last?.closeTime ?? "09:00";
    const [openHour = 9, openMinute = 0] = openTime.split(":").map(Number);
    const closeMinutes = Math.min(openHour * 60 + openMinute + 240, 23 * 60 + 59);
    if (closeMinutes <= openHour * 60 + openMinute) {
      toast("error", "There is no remaining time in this day for another shift.");
      return;
    }
    const closeTime = `${String(Math.floor(closeMinutes / 60)).padStart(2, "0")}:${String(closeMinutes % 60).padStart(2, "0")}`;
    update(dayOfWeek, { shifts: [...(day?.shifts ?? []), { openTime, closeTime }] });
  }

  async function save() {
    setSaving(true);
    try {
      const payload = hours.flatMap((day) => day.isClosed
        ? [{ dayOfWeek: day.dayOfWeek, sequence: 0, openTime: "00:00", closeTime: "23:59", isClosed: true }]
        : day.shifts.map((shift, sequence) => ({ ...shift, dayOfWeek: day.dayOfWeek, sequence, isClosed: false })));
      await api.put("/api/v1/ops/business-hours", { locationId, hours: payload });
      toast("success", "Operating hours saved");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const fields = (err as { fields?: Record<string, string> }).fields;
      const detail = fields ? Object.values(fields).join(" ") : null;
      toast("error", detail || (err instanceof Error ? err.message : "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base">Operating hours</CardTitle>
          <Button size="sm" onClick={() => void save()} disabled={saving || loading}>{saving ? "Saving…" : "Save hours"}</Button>
        </div>
        <Muted>Add multiple shifts to represent closed gaps such as lunch breaks. Bookings must fit entirely within one shift.</Muted>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Day</TableHead>
              <TableHead>Closed?</TableHead>
               <TableHead>Shifts</TableHead>
               <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hours.map((h) => (
              <TableRow key={h.dayOfWeek}>
                <TableCell className="font-medium">{DOWS[h.dayOfWeek]}</TableCell>
                <TableCell>
                  <Switch
                    aria-label={`Closed on ${DOWS[h.dayOfWeek]}`}
                    checked={h.isClosed}
                    onCheckedChange={(v) => update(h.dayOfWeek, { isClosed: v })}
                  />
                </TableCell>
                <TableCell>
                  {h.isClosed ? <span className="text-sm text-muted-foreground">Closed all day</span> : (
                    <div className="space-y-2">
                      {h.shifts.map((shift, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input type="time" value={shift.openTime} onChange={(e) => updateShift(h.dayOfWeek, index, { openTime: e.target.value })} className="w-32" />
                          <span className="text-muted-foreground">to</span>
                          <Input type="time" value={shift.closeTime} onChange={(e) => updateShift(h.dayOfWeek, index, { closeTime: e.target.value })} className="w-32" />
                          {h.shifts.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => update(h.dayOfWeek, { shifts: h.shifts.filter((_, shiftIndex) => shiftIndex !== index) })}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </div>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                 {!h.isClosed && <Button type="button" size="sm" variant="outline" onClick={() => addShift(h.dayOfWeek)}><Plus className="h-3.5 w-3.5" /> Add shift</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function defaultHours(): DailyHours[] {
  return [0, 1, 2, 3, 4, 5, 6].map((d) => ({
    dayOfWeek: d,
    isClosed: d === 0,
    shifts: [{ openTime: "09:00", closeTime: "18:00" }],
  }));
}

/* ---------- Blackouts ---------- */

function BlackoutsTab({ locationId, timezone }: { locationId: number; timezone: string }) {
  const { toast } = useToast();
  const [rows, setRows] = useState<BlackoutRow[]>([]);
  const [series, setSeries] = useState<BlackoutSeriesRow[]>([]);
  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [editing, setEditing] = useState<BlackoutRow | null | undefined>(undefined);
  const [editingSeries, setEditingSeries] = useState<BlackoutSeriesRow | null | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const now = new Date();
    const horizon = new Date(now.getTime() + 180 * 86400_000);
    Promise.all([
      api.get<BlackoutRow[]>(`/api/v1/ops/blackouts?locationId=${locationId}&from=${now.toISOString()}&to=${horizon.toISOString()}`),
      api.get<BlackoutSeriesRow[]>(`/api/v1/ops/blackout-series?locationId=${locationId}`),
      api.get<ResourceRow[]>(`/api/v1/ops/resources?locationId=${locationId}`),
    ]).then(([occurrences, rules, resourceRows]) => {
      setRows(occurrences); setSeries(rules); setResources(resourceRows);
    }).catch(() => { setRows([]); setSeries([]); setResources([]); });
  }, [locationId, refreshKey]);

  const refresh = () => setRefreshKey((key) => key + 1);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Recurring blackout series</CardTitle>
            <Button size="sm" onClick={() => setEditingSeries(null)}><CalendarDays className="h-3.5 w-3.5" /> New series</Button>
          </div>
          <Muted>Weekly closures use {timezone} local time and are materialized 180 days ahead.</Muted>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Schedule</TableHead><TableHead>Scope</TableHead><TableHead>Rule</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {series.map((rule) => <TableRow key={rule.id}>
                <TableCell>{rule.startLocalTime}–{rule.endLocalTime}<div className="text-xs text-muted-foreground">from {rule.startLocalDate}</div></TableCell>
                <TableCell>{rule.resourceName ?? "Whole location"}</TableCell>
                <TableCell className="max-w-xs text-xs text-muted-foreground">{rule.rrule.replace("RRULE:", "")}</TableCell>
                <TableCell><Badge className={rule.status === "CANCELLED" ? "border-slate-200 bg-slate-50 text-slate-600" : undefined}>{rule.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {rule.status === "ACTIVE" && <><Button size="sm" variant="ghost" onClick={() => setEditingSeries(rule)}><Pencil className="h-3.5 w-3.5" /></Button><Button size="sm" variant="ghost" onClick={async () => {
                    if (!confirm("Cancel this series and remove all future occurrences?")) return;
                    try { await api.delete(`/api/v1/ops/blackout-series/${rule.id}`); toast("success", "Series cancelled"); refresh(); } catch (err) { toast("error", err instanceof Error ? err.message : "Cancel failed"); }
                  }}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></>}
                </TableCell>
              </TableRow>)}
              {series.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No recurring series.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2"><CardTitle className="text-base">Upcoming occurrences</CardTitle><Button size="sm" onClick={() => setEditing(null)}><Plus className="h-3.5 w-3.5" /> New single blackout</Button></div>
          <Muted>Series occurrences show their source. Edit or delete a series from the table above.</Muted>
        </CardHeader>
        <CardContent><Table>
          <TableHeader><TableRow><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Scope</TableHead><TableHead>Source</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((blackout) => <TableRow key={blackout.id}>
              <TableCell>{formatInZone(blackout.startsAtUtc, timezone)}</TableCell><TableCell>{formatInZone(blackout.endsAtUtc, timezone)}</TableCell>
              <TableCell>{blackout.resourceName ?? "Whole location"}</TableCell><TableCell><Badge>{blackout.source === "SERIES" ? `Series #${blackout.blackoutSeriesId}` : "Single"}</Badge></TableCell>
              <TableCell className="text-muted-foreground">{blackout.reason ?? "—"}</TableCell>
              <TableCell className="text-right">{blackout.source === "SINGLE" && <><Button size="sm" variant="ghost" onClick={() => setEditing(blackout)}><Pencil className="h-3.5 w-3.5" /></Button><Button size="sm" variant="ghost" onClick={async () => {
                if (!confirm("Delete this blackout?")) return;
                try { await api.delete(`/api/v1/ops/blackouts/${blackout.id}`); toast("success", "Blackout deleted"); refresh(); } catch (err) { toast("error", err instanceof Error ? err.message : "Delete failed"); }
              }}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></>}</TableCell>
            </TableRow>)}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No upcoming blackouts.</TableCell></TableRow>}
          </TableBody>
        </Table></CardContent>
      </Card>
      <BlackoutSheet
        open={editing !== undefined}
        locationId={locationId}
        timezone={timezone}
        resources={resources}
        blackout={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={() => { refresh(); setEditing(undefined); }}
      />
      <BlackoutSeriesSheet open={editingSeries !== undefined} locationId={locationId} timezone={timezone} resources={resources} series={editingSeries ?? null} onClose={() => setEditingSeries(undefined)} onSaved={() => { refresh(); setEditingSeries(undefined); }} />
    </div>
  );
}

function BlackoutSheet({ open, locationId, timezone, resources, blackout, onClose, onSaved }: {
  open: boolean; locationId: number; timezone: string; resources: ResourceRow[]; blackout: BlackoutRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    startsAt: localInputValue(new Date().toISOString(), timezone),
    endsAt: localInputValue(new Date(Date.now() + 60 * 60_000).toISOString(), timezone),
    resourceId: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(blackout ? { startsAt: localInputValue(blackout.startsAtUtc, timezone), endsAt: localInputValue(blackout.endsAtUtc, timezone), resourceId: blackout.resourceId?.toString() ?? "", reason: blackout.reason ?? "" } : {
      startsAt: DateTime.now().setZone(timezone).toFormat("yyyy-MM-dd'T'HH:mm"), endsAt: DateTime.now().setZone(timezone).plus({ hours: 1 }).toFormat("yyyy-MM-dd'T'HH:mm"), resourceId: "", reason: "",
    });
  }, [blackout, timezone, open]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (new Date(form.endsAt).getTime() <= new Date(form.startsAt).getTime()) {
      toast("error", "End must be after start");
      return;
    }
    setSaving(true);
    try {
      const body = {
        locationId, resourceId: form.resourceId ? Number(form.resourceId) : null,
        startsAt: DateTime.fromISO(form.startsAt, { zone: timezone }).toUTC().toISO(),
        endsAt: DateTime.fromISO(form.endsAt, { zone: timezone }).toUTC().toISO(),
        reason: form.reason || null,
      };
      if (blackout) await api.patch(`/api/v1/ops/blackouts/${blackout.id}`, body);
      else await api.post("/api/v1/ops/blackouts", body);
      toast("success", blackout ? "Blackout updated" : "Blackout created");
      onSaved();
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title={blackout ? "Edit blackout" : "New blackout"} description={`Times are in ${timezone}`}>
      <form onSubmit={save} className="space-y-3">
        <FieldRow>
          <Label>Starts at</Label>
          <Input required type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
        </FieldRow>
        <FieldRow>
          <Label>Ends at</Label>
          <Input required type="datetime-local" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
        </FieldRow>
        <FieldRow><Label>Scope</Label><Select value={form.resourceId} onChange={(e) => setForm({ ...form, resourceId: e.target.value })}><option value="">Whole location</option>{resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}</Select></FieldRow>
        <FieldRow>
          <Label>Reason</Label>
          <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </FieldRow>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </Sheet>
  );
}

function BlackoutSeriesSheet({ open, locationId, timezone, resources, series, onClose, onSaved }: { open: boolean; locationId: number; timezone: string; resources: ResourceRow[]; series: BlackoutSeriesRow | null; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => defaultSeriesForm(timezone));
  useEffect(() => {
    setForm(series ? seriesToForm(series) : defaultSeriesForm(timezone));
  }, [series, timezone, open]);
  async function save(e: FormEvent) {
    e.preventDefault(); setSaving(true);
    try {
      const body = {
        locationId, timezone, resourceId: form.resourceId ? Number(form.resourceId) : null, startLocalDate: form.startLocalDate,
        startLocalTime: form.startLocalTime, endLocalTime: form.endLocalTime, reason: form.reason || null,
        recurrence: { interval: Number(form.interval), weekdays: form.weekdays, count: form.endMode === "count" ? Number(form.count) : null, untilLocalDate: form.endMode === "until" ? form.untilLocalDate : null },
        ...(series ? { effectiveFromLocalDate: form.effectiveFromLocalDate } : {}),
      };
      if (series) await api.patch(`/api/v1/ops/blackout-series/${series.id}`, body); else await api.post("/api/v1/ops/blackout-series", body);
      toast("success", series ? "Future series updated" : "Series created"); onSaved();
    } catch (err) { toast("error", err instanceof Error ? err.message : "Save failed"); } finally { setSaving(false); }
  }
  return <Sheet open={open} onClose={onClose} title={series ? "Edit future series" : "New recurring blackout"} description={`Weekly recurrence in ${timezone}`}>
    <form onSubmit={save} className="space-y-3">
      <FieldRow><Label>Scope</Label><Select value={form.resourceId} onChange={(e) => setForm({ ...form, resourceId: e.target.value })}><option value="">Whole location</option>{resources.map((resource) => <option key={resource.id} value={resource.id}>{resource.name}</option>)}</Select></FieldRow>
      {series && <FieldRow><Label>Apply changes from</Label><Input required type="date" value={form.effectiveFromLocalDate} onChange={(e) => setForm({ ...form, effectiveFromLocalDate: e.target.value })} /></FieldRow>}
      <FieldRow><Label>First recurrence date</Label><Input required type="date" value={form.startLocalDate} onChange={(e) => setForm({ ...form, startLocalDate: e.target.value })} /></FieldRow>
      <div className="grid gap-3 sm:grid-cols-2"><FieldRow><Label>Start time</Label><Input required type="time" value={form.startLocalTime} onChange={(e) => setForm({ ...form, startLocalTime: e.target.value })} /></FieldRow><FieldRow><Label>End time</Label><Input required type="time" value={form.endLocalTime} onChange={(e) => setForm({ ...form, endLocalTime: e.target.value })} /></FieldRow></div>
      <FieldRow><Label>Repeat on</Label><div className="flex flex-wrap gap-1">{[1, 2, 3, 4, 5, 6, 7].map((day) => <button key={day} type="button" onClick={() => setForm({ ...form, weekdays: form.weekdays.includes(day) ? form.weekdays.filter((value) => value !== day) : [...form.weekdays, day] })} className={cn("rounded border px-2 py-1 text-xs", form.weekdays.includes(day) && "border-primary bg-primary text-primary-foreground")}>{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][day - 1]}</button>)}</div></FieldRow>
      <FieldRow><Label>Every</Label><div className="flex items-center gap-2"><Input className="w-20" type="number" min={1} max={52} value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} /><span className="text-sm">week(s)</span></div></FieldRow>
      <FieldRow><Label>Ends</Label><Select value={form.endMode} onChange={(e) => setForm({ ...form, endMode: e.target.value as "never" | "until" | "count" })}><option value="never">Never</option><option value="until">On date</option><option value="count">After occurrences</option></Select></FieldRow>
      {form.endMode === "until" && <Input required type="date" value={form.untilLocalDate} onChange={(e) => setForm({ ...form, untilLocalDate: e.target.value })} />}
      {form.endMode === "count" && <Input required type="number" min={1} max={1000} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} />}
      <FieldRow><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></FieldRow>
      <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving || form.weekdays.length === 0}>{saving ? "Saving…" : "Save series"}</Button></div>
    </form>
  </Sheet>;
}

function defaultSeriesForm(timezone: string) {
  const today = DateTime.now().setZone(timezone).toISODate()!;
  return { resourceId: "", startLocalDate: today, effectiveFromLocalDate: today, startLocalTime: "09:00", endLocalTime: "10:00", interval: "1", weekdays: [DateTime.now().setZone(timezone).weekday], endMode: "never" as "never" | "until" | "count", untilLocalDate: DateTime.now().setZone(timezone).plus({ months: 3 }).toISODate()!, count: "12", reason: "" };
}

function seriesToForm(series: BlackoutSeriesRow) {
  const values = new Map(series.rrule.replace("RRULE:", "").split(";").map((part) => part.split("=") as [string, string]));
  const dayMap: Record<string, number> = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 };
  const until = values.get("UNTIL"); const count = values.get("COUNT");
  return { resourceId: series.resourceId?.toString() ?? "", startLocalDate: series.startLocalDate, effectiveFromLocalDate: DateTime.now().setZone(series.timezone).toISODate()!, startLocalTime: series.startLocalTime, endLocalTime: series.endLocalTime, interval: values.get("INTERVAL") ?? "1", weekdays: (values.get("BYDAY") ?? "").split(",").map((day) => dayMap[day]).filter((day): day is number => day !== undefined), endMode: count ? "count" as const : until ? "until" as const : "never" as const, untilLocalDate: until ? `${until.slice(0, 4)}-${until.slice(4, 6)}-${until.slice(6, 8)}` : DateTime.now().setZone(series.timezone).plus({ months: 3 }).toISODate()!, count: count ?? "12", reason: series.reason ?? "" };
}

function localInputValue(iso: string, timezone: string) { return DateTime.fromISO(iso).setZone(timezone).toFormat("yyyy-MM-dd'T'HH:mm"); }
function formatInZone(iso: string, timezone: string) { return DateTime.fromISO(iso).setZone(timezone).toLocaleString(DateTime.DATETIME_MED); }

/* ---------- Users (admin) ---------- */

function UsersTab({ locations }: { locations: LocationRow[] }) {
  const [rows, setRows] = useState<UserRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get<UserRow[]>("/api/v1/users").then(setRows).catch(() => setRows([]));
  }, [refreshKey]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Users</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New user</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.firstName} {u.lastName}</TableCell>
                <TableCell className="font-mono text-xs">{u.email}</TableCell>
                <TableCell><Badge>{u.role}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</TableCell>
                <TableCell>{u.active ? "✓" : <Badge>disabled</Badge>}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button type="button" className="rounded p-1 hover:bg-accent" onClick={() => setEditing(u)} aria-label="Edit user"><Pencil className="h-3.5 w-3.5" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No users.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <UserSheet
        open={creating}
        initial={null}
        locations={locations}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); setRefreshKey((k) => k + 1); }}
      />
      <UserSheet open={editing != null} initial={editing} locations={locations} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setRefreshKey((k) => k + 1); }} />
    </Card>
  );
}

interface CalendarTokenRow { id: number; label: string; createdAt: string; lastUsedAt: string | null; revokedAt: string | null; }

function UserSheet({ open, initial, locations, onClose, onSaved }: { open: boolean; initial: UserRow | null; locations: LocationRow[]; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ email: "", firstName: "", lastName: "", role: "STAFF" as "ADMIN" | "MANAGER" | "STAFF", password: "", active: true, memberships: [] as Array<{ locationId: number; scope: "READ" | "MANAGE" }> });
  const [saving, setSaving] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [tokens, setTokens] = useState<CalendarTokenRow[]>([]);

  useEffect(() => {
    setForm(initial ? { email: initial.email, firstName: initial.firstName, lastName: initial.lastName, role: initial.role, password: "", active: initial.active, memberships: initial.memberships } : { email: "", firstName: "", lastName: "", role: "STAFF", password: "", active: true, memberships: [] });
    setResetPassword("");
    if (initial && open) void loadTokens(initial.id); else setTokens([]);
  }, [initial, open]);

  async function loadTokens(userId: number) { try { setTokens(await api.get<CalendarTokenRow[]>(`/api/v1/users/${userId}/calendar-tokens`)); } catch { setTokens([]); } }

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, memberships: form.role === "ADMIN" ? [] : form.memberships };
      if (initial) await api.patch(`/api/v1/users/${initial.id}`, { email: payload.email, firstName: payload.firstName, lastName: payload.lastName, role: payload.role, active: payload.active, memberships: payload.memberships });
      else await api.post("/api/v1/users", payload);
      toast("success", initial ? "User updated" : "User created");
      onSaved();
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title={initial ? "Edit user" : "New user"}>
      <form onSubmit={save} className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
          <FieldRow>
            <Label>First name</Label>
            <Input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Last name</Label>
            <Input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </FieldRow>
        </div>
        <FieldRow>
          <Label>Email</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FieldRow>
        <FieldRow>
          <Label>Role</Label>
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as "ADMIN" | "MANAGER" | "STAFF" })}>
            <option value="ADMIN">ADMIN</option>
            <option value="MANAGER">MANAGER</option>
            <option value="STAFF">STAFF</option>
          </Select>
        </FieldRow>
        {!initial && <FieldRow><Label>Initial password</Label><Input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /><Muted>Share this securely; the user should change it after login.</Muted></FieldRow>}
        {form.role !== "ADMIN" && <FieldRow><Label>Assigned locations</Label><div className="space-y-2 rounded-lg border p-3">{locations.map((location) => {
          const membership = form.memberships.find((item) => item.locationId === location.id);
          return <div key={location.id} className="flex items-center gap-2"><input type="checkbox" checked={!!membership} onChange={(e) => setForm({ ...form, memberships: e.target.checked ? [...form.memberships, { locationId: location.id, scope: "MANAGE" }] : form.memberships.filter((item) => item.locationId !== location.id) })} /><span className="flex-1 text-sm">{location.name}</span>{membership && <Select className="w-28" value={membership.scope} onChange={(e) => setForm({ ...form, memberships: form.memberships.map((item) => item.locationId === location.id ? { ...item, scope: e.target.value as "READ" | "MANAGE" } : item) })}><option value="READ">Read</option><option value="MANAGE">Manage</option></Select>}</div>;
        })}</div></FieldRow>}
        {initial && <div className="flex items-center gap-3"><Switch id="user-active" checked={form.active} onCheckedChange={(active) => setForm({ ...form, active })} /><Label htmlFor="user-active">Account active</Label></div>}
        {initial && <FieldRow><Label>Administrator password reset</Label><div className="flex gap-2"><Input type="password" minLength={8} placeholder="New temporary password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} /><Button type="button" variant="outline" disabled={resetPassword.length < 8} onClick={async () => { try { await api.post(`/api/v1/users/${initial.id}/reset-password`, { password: resetPassword }); setResetPassword(""); toast("success", "Password reset and sessions revoked"); } catch (error) { toast("error", error instanceof Error ? error.message : "Reset failed"); } }}>Reset</Button></div></FieldRow>}
        {initial && <FieldRow><Label>Calendar feeds</Label><div className="space-y-2">{tokens.map((token) => <div key={token.id} className="flex items-center gap-2 rounded border p-2 text-xs"><CalendarDays className="h-4 w-4" /><span className="flex-1">{token.label}{token.revokedAt ? " · revoked" : token.lastUsedAt ? ` · used ${formatDateTime(token.lastUsedAt)}` : " · unused"}</span>{!token.revokedAt && <Button type="button" size="sm" variant="ghost" onClick={async () => { await api.delete(`/api/v1/users/${initial.id}/calendar-tokens/${token.id}`); await loadTokens(initial.id); toast("success", "Feed revoked"); }}>Revoke</Button>}</div>)}<Button type="button" size="sm" variant="outline" onClick={async () => { const label = prompt("Calendar feed label", "Personal calendar")?.trim(); if (!label) return; try { const created = await api.post<{ feedUrl: string }>(`/api/v1/users/${initial.id}/calendar-tokens`, { label }); await navigator.clipboard.writeText(created.feedUrl); await loadTokens(initial.id); toast("success", "New feed URL copied. It will not be shown again."); } catch (error) { toast("error", error instanceof Error ? error.message : "Feed creation failed"); } }}><CalendarDays className="h-3.5 w-3.5" /> Create feed</Button></div></FieldRow>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : initial ? "Save user" : "Create user"}</Button>
        </div>
      </form>
    </Sheet>
  );
}

// Avoid unused-import noise
void RefreshCw;
void buttonVariants;

/* ---------- Add-ons ---------- */

interface AddonRow {
  id: number; locationId: number; serviceId: number; code: string; name: string;
  description: string | null; priceCents: number; impactMinutes: number; active: boolean;
}

function AddonsTab({ locationId }: { locationId: number }) {
  const { toast } = useToast();
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [editing, setEditing] = useState<AddonRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [serviceFilter, setServiceFilter] = useState<number | "all">("all");

  useEffect(() => {
    api.get<ServiceRow[]>(`/api/v1/ops/services?locationId=${locationId}`).then(setServices).catch(() => setServices([]));
  }, [locationId]);

  useEffect(() => {
    api.get<AddonRow[]>(`/api/v1/ops/addons?locationId=${locationId}&includeArchived=true`).then(setAddons).catch(() => setAddons([]));
  }, [locationId, refreshKey]);

  const visible = addons.filter((a) => serviceFilter === "all" || a.serviceId === serviceFilter);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Add-ons</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={String(serviceFilter)} onChange={(e) => setServiceFilter(e.target.value === "all" ? "all" : Number(e.target.value))}>
              <option value="all">All services</option>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
            <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New add-on</Button>
          </div>
        </div>
        <Muted>Add-ons attach to a parent service and may add impact_minutes to the booking duration.</Muted>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Parent service</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">+Min</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((a) => {
              const parent = services.find((s) => s.id === a.serviceId);
              return (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{a.code}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell className="text-muted-foreground">{parent?.name ?? "—"}</TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground">{a.description ?? ""}</TableCell>
                  <TableCell className="text-right tabular-nums">{(a.priceCents / 100).toFixed(2)}</TableCell>
                  <TableCell className="text-right tabular-nums">{a.impactMinutes}</TableCell>
                  <TableCell>{a.active ? "✓" : <Badge>archived</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <button className="rounded p-1 hover:bg-accent" onClick={() => setEditing(a)} aria-label="Edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    {a.active && (
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-accent"
                        onClick={async () => {
                          if (!confirm("Archive this add-on?")) return;
                          try {
                            await api.delete(`/api/v1/ops/addons/${a.id}`);
                            toast("success", `${a.name} archived`);
                            setRefreshKey((k) => k + 1);
                          } catch (e) {
                            toast("error", e instanceof Error ? e.message : "Archive failed");
                          }
                        }}
                        aria-label="Archive"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {visible.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No add-ons yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      <AddonSheet
        open={creating || editing != null}
        initial={editing}
        locationId={locationId}
        services={services}
        onClose={() => { setEditing(null); setCreating(false); }}
        onSaved={() => { setRefreshKey((k) => k + 1); setEditing(null); setCreating(false); }}
      />
    </Card>
  );
}

function AddonSheet({
  open, initial, locationId, services, onClose, onSaved,
}: {
  open: boolean;
  initial: AddonRow | null;
  locationId: number;
  services: ServiceRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const isEdit = initial != null;
  const [form, setForm] = useState({
    serviceId: initial?.serviceId ?? (services[0]?.id ?? 0),
    code: initial?.code ?? "",
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    priceCents: initial?.priceCents ?? 500,
    impactMinutes: initial?.impactMinutes ?? 0,
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) {
      setForm({
        serviceId: initial.serviceId,
        code: initial.code,
        name: initial.name,
        description: initial.description ?? "",
        priceCents: initial.priceCents,
        impactMinutes: initial.impactMinutes,
        active: initial.active,
      });
    } else {
      setForm({
        serviceId: services[0]?.id ?? 0,
        code: "", name: "", description: "",
        priceCents: 500, impactMinutes: 0, active: true,
      });
    }
  }, [initial, open, services]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.serviceId) { toast("error", "Pick a parent service"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/api/v1/ops/addons/${initial!.id}`, {
          code: form.code, name: form.name, description: form.description || null,
          priceCents: form.priceCents, impactMinutes: form.impactMinutes, active: form.active,
        });
      } else {
        await api.post("/api/v1/ops/addons", {
          locationId, serviceId: form.serviceId, code: form.code, name: form.name,
          description: form.description || null, priceCents: form.priceCents,
          impactMinutes: form.impactMinutes, active: form.active,
        });
      }
      toast("success", isEdit ? "Add-on updated" : "Add-on created");
      onSaved();
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title={isEdit ? "Edit add-on" : "New add-on"} description="Each add-on is bound to one parent service. Add-ons add to the booking duration when impact_minutes > 0.">
      <form onSubmit={save} className="space-y-3">
        {!isEdit && (
          <div>
            <Label>Parent service</Label>
            <Select value={form.serviceId?.toString() ?? ""} onChange={(e) => setForm({ ...form, serviceId: Number(e.target.value) })}>
              {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Code</Label>
            <Input required maxLength={40} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SCENT, TIRE, WAX…" />
          </div>
          <div>
            <Label>Display name</Label>
            <Input required maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Input value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Price (minor units)</Label>
            <Input required type="number" min={0} value={form.priceCents} onChange={(e) => setForm({ ...form, priceCents: Number(e.target.value) })} />
            <p className="text-xs text-muted-foreground">{(form.priceCents / 100).toFixed(2)} in major units</p>
          </div>
          <div>
            <Label>Impact (minutes added)</Label>
            <Input required type="number" min={0} max={360} value={form.impactMinutes} onChange={(e) => setForm({ ...form, impactMinutes: Number(e.target.value) })} />
            <p className="text-xs text-muted-foreground">0 if it doesn't add duration</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch id="addon-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
          <Label htmlFor="addon-active">Active (bookable)</Label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/* ---------- Cancellation policy ---------- */

interface CancellationSettings {
  locationId: number;
  enabled: boolean;
  cancellationWindowHours: number;
  cancellationFeeCents: number;
  noShowFeeCents: number;
}

function CancellationTab({ locationId }: { locationId: number }) {
  const { toast } = useToast();
  const [settings, setSettings] = useState<CancellationSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<CancellationSettings>(`/api/v1/cancellation/settings/${locationId}`)
      .then(setSettings)
      .catch((e) => toast("error", e.message));
  }, [locationId, toast]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put<CancellationSettings>(`/api/v1/cancellation/settings/${locationId}`, settings);
      setSettings(r);
      toast("success", "Cancellation policy updated");
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cancellation policy</CardTitle>
        <Muted>
          Cancellations within the window incur the cancellation fee. No-shows incur the no-show fee.
          Both are recorded as payments on the booking.
        </Muted>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <FieldRow className="sm:col-span-2 flex items-center gap-3">
            <Switch
              id="policy-enabled"
              checked={settings.enabled}
              onCheckedChange={(v) => setSettings({ ...settings, enabled: v })}
            />
            <Label htmlFor="policy-enabled">Cancellation policy active for this location</Label>
          </FieldRow>
          <FieldRow>
            <Label>Cancellation window (hours before start)</Label>
            <Input
              type="number"
              min={0}
              max={720}
              value={settings.cancellationWindowHours}
              onChange={(e) => setSettings({ ...settings, cancellationWindowHours: Number(e.target.value) })}
            />
            <Muted>0 means the policy never applies (always free cancellation).</Muted>
          </FieldRow>
          <FieldRow>
            <Label>Cancellation fee (minor units)</Label>
            <Input
              type="number"
              min={0}
              value={settings.cancellationFeeCents}
              onChange={(e) => setSettings({ ...settings, cancellationFeeCents: Number(e.target.value) })}
            />
            <Muted>Recorded as a payment with method=OTHER when applied.</Muted>
          </FieldRow>
          <FieldRow>
            <Label>No-show fee (minor units)</Label>
            <Input
              type="number"
              min={0}
              value={settings.noShowFeeCents}
              onChange={(e) => setSettings({ ...settings, noShowFeeCents: Number(e.target.value) })}
            />
            <Muted>Charged when staff marks a booking as NO_SHOW.</Muted>
          </FieldRow>
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save policy"}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/* ---------- Recurring bookings ---------- */

interface RecurringRow {
  id: number;
  publicCode: string;
  locationId: number;
  rrule: string;
  dtstart: string;
  dtend: string | null;
  horizonDays: number;
  status: "ACTIVE" | "PAUSED" | "CANCELLED";
  createdAt: string;
}

function RecurringTab() {
  const { toast } = useToast();
  const [rows, setRows] = useState<RecurringRow[]>([]);
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const r = await api.get<RecurringRow[]>("/api/v1/recurring");
      setRows(r);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Failed to load");
    }
  }

   useEffect(() => { void load(); }, []);

  async function cancelSeries(r: RecurringRow) {
    if (!confirm(`Cancel recurring ${r.publicCode}? Future occurrences will be marked CANCELLED.`)) return;
    try {
      const res = await api.delete<{ occurrencesCancelled: number }>(`/api/v1/recurring/${r.id}`);
      toast("success", `Series cancelled (${res.occurrencesCancelled} future occurrence(s))`);
      await load();
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Cancel failed");
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Recurring bookings</CardTitle>
          <Button size="sm" onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New series</Button>
        </div>
        <Muted>Each series expands into materialized bookings on creation. Cancelling stops future occurrences.</Muted>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>RRULE</TableHead>
              <TableHead>Starts</TableHead>
              <TableHead>Horizon</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono">{r.publicCode}</TableCell>
                <TableCell className="max-w-xs truncate font-mono text-xs">{r.rrule}</TableCell>
                <TableCell>{formatDateTime(r.dtstart)}</TableCell>
                <TableCell>{r.horizonDays}d</TableCell>
                <TableCell>
                  {r.status === "ACTIVE" ? <span className="text-emerald-600">ACTIVE</span> :
                   r.status === "CANCELLED" ? <Badge>CANCELLED</Badge> :
                   <Badge>PAUSED</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "ACTIVE" && (
                    <Button size="sm" variant="outline" onClick={() => void cancelSeries(r)}>Cancel</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No recurring series yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
      <RecurringCreateSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); void load(); }}
      />
    </Card>
  );
}

function RecurringCreateSheet({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { toast } = useToast();
  const [locationId, setLocationId] = useState<number | null>(null);
  const [resourceId, setResourceId] = useState<number | null>(null);
  const [resources, setResources] = useState<{ id: number; name: string; type: string }[]>([]);
   const [serviceMode, setServiceMode] = useState<"STATIONARY" | "MOBILE">("STATIONARY");
  const [customer, setCustomer] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [vehicle, setVehicle] = useState({ plate: "", make: "", model: "" });
  const [serviceAddress, setServiceAddress] = useState({ line1: "", city: "", countryCode: "" });
  const [rrule, setRrule] = useState("FREQ=WEEKLY;BYDAY=MO,WE,FR;COUNT=12");
  const [dtstart, setDtstart] = useState(() => new Date().toISOString().slice(0, 16));
  const [horizonDays, setHorizonDays] = useState(90);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get<{ id: number; name: string; timezone: string; currency: string }[]>("/api/v1/locations")
      .then((arr) => {
        if (arr[0] && !locationId) setLocationId(arr[0].id);
      });
  }, [open, locationId]);

  useEffect(() => {
    if (!locationId) return;
    api.get<{ id: number; name: string; type: string }[]>(`/api/v1/ops/resources?locationId=${locationId}`)
      .then(setResources);
  }, [locationId]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!locationId) return;
    setSaving(true);
    try {
      const body = {
        locationId,
        resourceId,
        serviceMode,
        customer: { ...customer, email: customer.email || null, phone: customer.phone || null },
        vehicle: vehicle.plate ? vehicle : undefined,
        serviceAddress: serviceMode === "MOBILE" && serviceAddress.line1
          ? { ...serviceAddress, city: serviceAddress.city || null, countryCode: serviceAddress.countryCode || null }
          : undefined,
        rrule,
        dtstart: new Date(dtstart).toISOString(),
        horizonDays,
      };
      const r = await api.post<{ ruleId: number; publicCode: string; materialized: number }>("/api/v1/recurring", body);
      toast("success", `Created ${r.publicCode} (${r.materialized} occurrence(s) materialized)`);
      onCreated();
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="New recurring series">
      <form onSubmit={save} className="space-y-3">
        <FieldRow>
          <Label>RRULE</Label>
          <Input required value={rrule} onChange={(e) => setRrule(e.target.value)} />
          <Muted>RFC 5545 syntax. Example: FREQ=WEEKLY;BYDAY=MO,WE,FR;COUNT=12</Muted>
        </FieldRow>
        <div className="grid gap-2 sm:grid-cols-2">
          <FieldRow>
            <Label>First occurrence (UTC)</Label>
            <Input type="datetime-local" value={dtstart} onChange={(e) => setDtstart(e.target.value)} />
          </FieldRow>
          <FieldRow>
            <Label>Materialize horizon (days)</Label>
            <Input type="number" min={1} max={365} value={horizonDays} onChange={(e) => setHorizonDays(Number(e.target.value))} />
          </FieldRow>
        </div>
        <FieldRow>
          <Label>Resource</Label>
          <Select value={resourceId?.toString() ?? ""} onChange={(e) => setResourceId(Number(e.target.value))}>
            {resources.filter((r) => serviceMode === "MOBILE" ? r.type === "STAFF_MEMBER" : r.type !== "STAFF_MEMBER").map((r) => (
              <option key={r.id} value={r.id}>{r.name} ({r.type})</option>
            ))}
          </Select>
        </FieldRow>
        <div className="grid gap-2 sm:grid-cols-2">
          <FieldRow>
            <Label>Service mode</Label>
            <Select value={serviceMode} onChange={(e) => setServiceMode(e.target.value as "STATIONARY" | "MOBILE")}>
              <option value="STATIONARY">In-bay</option>
              <option value="MOBILE">Mobile</option>
            </Select>
          </FieldRow>
          <FieldRow>
            <Label>Customer first name</Label>
            <Input value={customer.firstName} onChange={(e) => setCustomer({ ...customer, firstName: e.target.value })} />
          </FieldRow>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <FieldRow>
            <Label>Customer last name</Label>
            <Input value={customer.lastName} onChange={(e) => setCustomer({ ...customer, lastName: e.target.value })} />
          </FieldRow>
          <FieldRow>
            <Label>Customer email</Label>
            <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
          </FieldRow>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          <FieldRow><Label>Plate</Label><Input value={vehicle.plate} onChange={(e) => setVehicle({ ...vehicle, plate: e.target.value })} /></FieldRow>
          <FieldRow><Label>Make</Label><Input value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} /></FieldRow>
          <FieldRow><Label>Model</Label><Input value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} /></FieldRow>
        </div>
        {serviceMode === "MOBILE" && (
          <div className="grid gap-2 sm:grid-cols-3">
            <FieldRow><Label>Address line 1</Label><Input value={serviceAddress.line1} onChange={(e) => setServiceAddress({ ...serviceAddress, line1: e.target.value })} /></FieldRow>
            <FieldRow><Label>City</Label><Input value={serviceAddress.city} onChange={(e) => setServiceAddress({ ...serviceAddress, city: e.target.value })} /></FieldRow>
            <FieldRow><Label>Country</Label><Input maxLength={2} value={serviceAddress.countryCode} onChange={(e) => setServiceAddress({ ...serviceAddress, countryCode: e.target.value.toUpperCase() })} /></FieldRow>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
           <Button type="submit" disabled={saving || !locationId || !resourceId}>
            {saving ? "Creating…" : "Create series"}
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
